import React from "react";
import { Text } from "@mantine/core";
import { openConfirmModal, openModal } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import t from "../../../i18n/i18next";
import { ALERTS_USE_TOASTS } from "../../utilities/FeatureFlags";

export enum AlertIcon {
    WARNING,
    ERROR,
    SUCCESS,
    INFO,
}

type AlertButton = {
    text: string;
    value: string | null;
    visible?: boolean;
};

type AlertButtons = Record<string, AlertButton>;

const getAlertColor = (iconValue?: AlertIcon): string => {
    switch (iconValue) {
        case AlertIcon.WARNING:
            return "orange";
        case AlertIcon.ERROR:
            return "red";
        case AlertIcon.SUCCESS:
            return "green";
        case AlertIcon.INFO:
        default:
            return "blue";
    }
};

export function alert(
    text: string,
    title?: string,
    iconValue?: AlertIcon,
    callback?: () => void
) {
    let buttons: AlertButtons | undefined;
    if (callback) {
        buttons = {
            ok: {
                text: t("OK"),
                value: "ok",
            },
            cancel: {
                text: t("Cancel"),
                value: null,
                visible: true,
            },
        };
    }

    if (callback && buttons) {
        openConfirmModal({
            title,
            children: <Text size="sm">{text}</Text>,
            labels: {
                confirm: buttons.ok.text,
                cancel: buttons.cancel.text,
            },
            confirmProps: { color: getAlertColor(iconValue) },
            onConfirm: callback,
        });
        return;
    }

    openModal({
        title,
        children: <Text size="sm">{text}</Text>,
        centered: true,
    });
}

export function notifyWithAlertOrToast(
    text: string,
    title?: string,
    iconValue?: AlertIcon,
    callback?: () => void
) {
    if (ALERTS_USE_TOASTS()) {
        let color = "blue";
        let severityClassName = "vc-notification-info";
        switch (+iconValue) {
            case AlertIcon.WARNING:
                color = "orange";
                severityClassName = "vc-notification-warning";
                break;
            case AlertIcon.ERROR:
                color = "red";
                severityClassName = "vc-notification-error";
                break;
            case AlertIcon.SUCCESS:
                color = "green";
                severityClassName = "vc-notification-success";
                break;
            case AlertIcon.INFO:
                color = "blue";
                severityClassName = "vc-notification-info";
                break;
            default:
                color = "blue";
                severityClassName = "vc-notification-info";
        }
        notifications.show({
            title,
            message: text,
            color,
            autoClose: 6000,
            withBorder: true,
            classNames: {
                root: `vc-notification ${severityClassName}`,
                title: "vc-notification-title",
                description: "vc-notification-description",
                closeButton: "vc-notification-close",
            },
        });
        if (callback) {
            callback();
        }
        return;
    }

    return alert(text, title, iconValue, callback);
}
